// Customer portal — Support / Feedback link + modal.
//
// Steve 2026-05-22 — subtle "Something not right?" link in a small footer
// strip beneath the main canvas. Opens a simpler modal than the retailer
// equivalent: two prompts (what went wrong / what would help), optional
// screenshot, submit. Posts to the shared `/api/portal/feedback` endpoint
// with `portal=customer`, so the admin Feedback page sees all submissions
// from a single backing table.

import type { CustomerProject } from "../types";

interface CustomerLike {
  id?: string;
  firstName?: string;
  lastName?: string;
  email?: string | null;
}

/**
 * Build the small footer strip with the "Something not right?" link.
 * Returns the footer element so the shell can append it after `main`.
 */
export function buildFeedbackFooter(project: CustomerProject | null): HTMLElement {
  const footer = document.createElement("footer");
  footer.className = "cust-feedback-footer";
  footer.innerHTML = `
    <button type="button" class="cust-feedback-footer__link" data-role="open-feedback">
      Something not right?
    </button>
  `;
  const btn = footer.querySelector<HTMLButtonElement>("[data-role='open-feedback']");
  btn?.addEventListener("click", () => openFeedbackModal(project?.customer ?? null));
  return footer;
}

/**
 * Open the feedback modal. Idempotent — re-using the same <dialog> avoids
 * piling up overlays if the user clicks the link repeatedly.
 */
function openFeedbackModal(customer: CustomerLike | null): void {
  let dialog = document.querySelector<HTMLDialogElement>(".cust-feedback-modal");
  if (!dialog) {
    dialog = buildModal();
    document.body.appendChild(dialog);
  }
  resetModal(dialog);
  // Stash the customer reference on the dialog so the submit handler
  // can read it without closures.
  (dialog as HTMLDialogElement & { __kosCustomer?: CustomerLike | null }).__kosCustomer = customer;
  if (typeof dialog.showModal === "function") {
    dialog.showModal();
  } else {
    dialog.setAttribute("open", "");
  }
  window.setTimeout(() => {
    dialog?.querySelector<HTMLTextAreaElement>("[data-role='what-wrong']")?.focus();
  }, 60);
}

function buildModal(): HTMLDialogElement {
  const dialog = document.createElement("dialog");
  dialog.className = "cust-feedback-modal";
  dialog.innerHTML = `
    <form class="cust-feedback-modal__form" method="dialog" data-role="form">
      <header class="cust-feedback-modal__head">
        <h2>Tell us what's up</h2>
        <button type="button" class="cust-feedback-modal__close" data-role="close" aria-label="Close">&times;</button>
      </header>
      <div class="cust-feedback-modal__body">
        <p class="cust-feedback-modal__intro">
          Anything that's not working as expected — or a suggestion for what would help. We'll read every word.
        </p>
        <label class="cust-feedback-modal__field">
          <span>What went wrong?</span>
          <textarea data-role="what-wrong" rows="4" maxlength="4000" placeholder="Describe what happened…" required></textarea>
        </label>
        <label class="cust-feedback-modal__field">
          <span>What would help? <em>(optional)</em></span>
          <textarea data-role="what-help" rows="3" maxlength="4000" placeholder="If you have an idea or a fix in mind, tell us."></textarea>
        </label>
        <label class="cust-feedback-modal__field">
          <span>Screenshot <em>(optional · PNG/JPG/WEBP · max 5 MB)</em></span>
          <input type="file" data-role="screenshot" accept="image/png,image/jpeg,image/webp" />
          <span class="cust-feedback-modal__screenshot-name" data-role="screenshot-name" hidden></span>
        </label>
        <div class="cust-feedback-modal__error" data-role="error" hidden></div>
      </div>
      <footer class="cust-feedback-modal__foot">
        <button type="button" class="cust-feedback-modal__btn" data-role="cancel">Cancel</button>
        <button type="submit" class="cust-feedback-modal__btn cust-feedback-modal__btn--primary" data-role="submit">
          <span data-role="submit-label">Send</span>
        </button>
      </footer>
      <div class="cust-feedback-modal__confirm" data-role="confirm" hidden>
        <h3>Thanks — we've got it.</h3>
        <p>The platform team will read this and come back to you if we need anything.</p>
        <button type="button" class="cust-feedback-modal__btn cust-feedback-modal__btn--primary" data-role="confirm-close">Close</button>
      </div>
    </form>
  `;

  // Close on backdrop click.
  dialog.addEventListener("click", (e) => {
    if (e.target === dialog) dialog.close();
  });
  dialog.querySelector<HTMLButtonElement>("[data-role='close']")?.addEventListener("click", () => dialog.close());
  dialog.querySelector<HTMLButtonElement>("[data-role='cancel']")?.addEventListener("click", () => dialog.close());
  dialog.querySelector<HTMLButtonElement>("[data-role='confirm-close']")?.addEventListener("click", () => dialog.close());

  const fileInput = dialog.querySelector<HTMLInputElement>("[data-role='screenshot']")!;
  const fileName  = dialog.querySelector<HTMLSpanElement>("[data-role='screenshot-name']")!;
  fileInput.addEventListener("change", () => {
    const f = fileInput.files?.[0];
    if (f) {
      fileName.hidden = false;
      fileName.textContent = `${f.name} · ${(f.size / 1024).toFixed(0)} KB`;
    } else {
      fileName.hidden = true;
      fileName.textContent = "";
    }
  });

  const form = dialog.querySelector<HTMLFormElement>("[data-role='form']")!;
  const submitBtn = dialog.querySelector<HTMLButtonElement>("[data-role='submit']")!;
  const submitLbl = dialog.querySelector<HTMLSpanElement>("[data-role='submit-label']")!;
  const errorEl   = dialog.querySelector<HTMLDivElement>("[data-role='error']")!;
  const confirm   = dialog.querySelector<HTMLDivElement>("[data-role='confirm']")!;
  const body      = dialog.querySelector<HTMLDivElement>(".cust-feedback-modal__body")!;
  const foot      = dialog.querySelector<HTMLElement>(".cust-feedback-modal__foot")!;
  let submitting = false;

  form.addEventListener("submit", (e) => {
    e.preventDefault();
    if (submitting) return;
    errorEl.hidden = true;
    errorEl.textContent = "";

    const whatWrong = dialog.querySelector<HTMLTextAreaElement>("[data-role='what-wrong']")!.value.trim();
    const whatHelp  = dialog.querySelector<HTMLTextAreaElement>("[data-role='what-help']")!.value.trim();
    if (!whatWrong) {
      errorEl.hidden = false;
      errorEl.textContent = "Tell us what went wrong before sending.";
      return;
    }
    const description = whatHelp
      ? `${whatWrong}\n\n— What would help —\n${whatHelp}`
      : whatWrong;

    const customer = (dialog as HTMLDialogElement & { __kosCustomer?: CustomerLike | null }).__kosCustomer;

    submitting = true;
    submitBtn.disabled = true;
    submitLbl.textContent = "Sending…";

    const fd = new FormData();
    fd.append("portal", "customer");
    fd.append("type", "general");
    fd.append("title", whatWrong.slice(0, 80));
    fd.append("description", description);
    if (customer) {
      if (customer.id) fd.append("customerId", customer.id);
      const fullName = `${customer.firstName ?? ""} ${customer.lastName ?? ""}`.trim();
      if (fullName) fd.append("submitterName", fullName);
      if (customer.email) fd.append("submitterEmail", customer.email);
    }
    const file = fileInput.files?.[0];
    if (file) fd.append("screenshot", file);

    void (async () => {
      try {
        const res = await fetch("/api/portal/feedback", {
          method: "POST",
          credentials: "include",
          body: fd,
        });
        const data = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: string };
        if (!res.ok || !data.ok) {
          throw new Error(data.error || `HTTP ${res.status}`);
        }
        body.hidden = true;
        foot.hidden = true;
        confirm.hidden = false;
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Could not send";
        errorEl.hidden = false;
        errorEl.textContent = `Couldn't send: ${msg}`;
      } finally {
        submitting = false;
        submitBtn.disabled = false;
        submitLbl.textContent = "Send";
      }
    })();
  });

  return dialog;
}

function resetModal(dialog: HTMLDialogElement): void {
  const whatWrong = dialog.querySelector<HTMLTextAreaElement>("[data-role='what-wrong']");
  const whatHelp  = dialog.querySelector<HTMLTextAreaElement>("[data-role='what-help']");
  const file      = dialog.querySelector<HTMLInputElement>("[data-role='screenshot']");
  const fileName  = dialog.querySelector<HTMLSpanElement>("[data-role='screenshot-name']");
  const errorEl   = dialog.querySelector<HTMLDivElement>("[data-role='error']");
  const body      = dialog.querySelector<HTMLDivElement>(".cust-feedback-modal__body");
  const foot      = dialog.querySelector<HTMLElement>(".cust-feedback-modal__foot");
  const confirm   = dialog.querySelector<HTMLDivElement>("[data-role='confirm']");
  if (whatWrong) whatWrong.value = "";
  if (whatHelp)  whatHelp.value  = "";
  if (file)      file.value      = "";
  if (fileName)  { fileName.hidden = true; fileName.textContent = ""; }
  if (errorEl)   { errorEl.hidden  = true; errorEl.textContent  = ""; }
  if (body)      body.hidden    = false;
  if (foot)      foot.hidden    = false;
  if (confirm)   confirm.hidden = true;
}
